import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock, Save } from "lucide-react";

function fmtMin(m) {
  const h = Math.floor(m / 60), mm = m % 60;
  return h ? `${h}h${mm ? String(mm).padStart(2, "0") : ""}` : `${mm}min`;
}

export default function CatalogoTempos() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    api.get("/catalogo-tempos").then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);

  const salvar = async (codigo) => {
    const v = parseInt(edits[codigo], 10);
    if (!v || v < 5) { toast.error("Tempo mínimo de 5 minutos"); return; }
    try {
      await api.put(`/admin/catalogo-tempos/${codigo}`, { tempo_base_min: v });
      toast.success(`${codigo} atualizado`);
      setEdits(e => { const n = { ...e }; delete n[codigo]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="aj-card p-10 text-center text-aj-text-muted">Carregando…</div>;

  const itens = data.itens.filter(i =>
    (!cat || i.categoria === cat) &&
    (!q || `${i.produto} ${i.variacao} ${i.codigo}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-6" data-testid="catalogo-tempos-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Inteligência Operacional</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Catálogo de Tempos de Montagem</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Referência oficial (planilha importada) usada para <b>previsão operacional</b> — nunca jornada, meta de horas ou ranking de velocidade.
          Margem de segurança: <b>{data.config.margem_pct}%</b> · Tempo genérico: <b>{fmtMin(data.config.tempo_generico_min)}</b>
        </p>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select value={cat} onChange={e => setCat(e.target.value)}
          className="border border-border rounded-md text-sm px-3 py-2 bg-white" data-testid="cat-filtro-categoria">
          <option value="">Todas as categorias</option>
          {data.categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input placeholder="Buscar produto ou código…" value={q} onChange={e => setQ(e.target.value)}
          className="w-64" data-testid="cat-busca" />
        <span className="text-xs text-aj-text-muted">{itens.length} item(ns)</span>
      </div>

      <div className="aj-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-[#F4F7FB]">
            <tr>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Código</th>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Categoria</th>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Produto / Variação</th>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Complexidade</th>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Tempo base</th>
              <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">c/ margem</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {itens.map(i => (
              <tr key={i.codigo} className="border-b border-border/60 last:border-0" data-testid={`cat-item-${i.codigo}`}>
                <td className="p-3 font-mono text-xs">{i.codigo}</td>
                <td className="p-3 text-xs">{i.categoria}</td>
                <td className="p-3"><b>{i.produto}</b>{i.variacao && <span className="text-aj-text-muted"> · {i.variacao}</span>}</td>
                <td className="p-3 text-xs">{i.complexidade}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min={5} className="h-8 w-20 text-sm"
                      value={edits[i.codigo] ?? i.tempo_base_min}
                      onChange={e => setEdits(s => ({ ...s, [i.codigo]: e.target.value }))}
                      data-testid={`cat-tempo-${i.codigo}`} />
                    <span className="text-xs text-aj-text-muted">min</span>
                  </div>
                </td>
                <td className="p-3 font-mono text-xs text-aj-blue-deep">
                  {fmtMin(Math.round((edits[i.codigo] ?? i.tempo_base_min) * (1 + data.config.margem_pct / 100)))}
                </td>
                <td className="p-3">
                  {edits[i.codigo] !== undefined && String(edits[i.codigo]) !== String(i.tempo_base_min) && (
                    <Button size="sm" className="h-8 text-xs btn-aj-primary" onClick={() => salvar(i.codigo)}
                      data-testid={`cat-salvar-${i.codigo}`}>
                      <Save size={12} /> Salvar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="aj-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-aj-orange" />
          <h2 className="font-display font-bold text-base text-aj-blue-deep">Adicionais de instalação / fixação na parede</h2>
        </div>
        <p className="text-xs text-aj-text-muted mb-3">Entram no tempo apenas quando incluídos na OS. Consideram parede em boas condições — não incluem elétrica, hidráulica ou recorte de móveis.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.adicionais.map(a => (
            <div key={a.nome} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm"
              data-testid={`cat-adicional-${a.nome.replace(/\s+/g, "-").toLowerCase()}`}>
              <span>{a.nome}</span>
              <span className="font-mono text-xs font-bold text-aj-blue-deep">{fmtMin(a.tempo_min)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
